import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SimpleBar from 'simplebar-react'
import { Download01 } from '@untitled-ui/icons-react'
import { BillingService } from '../../billing.service'
import { useCompany } from '../../Context'
import { downloadInvoiceStatement } from '../../invoiceDocument'
import { formatIDR, invoiceStatusBadge, subscriptionStatusBadge } from '../../billing.format'
import { memberStatusOf } from '../MyMemberStatusChip'
import QuotaUsage from './QuotaUsage'

// platform_fee_idr is the MONTHLY rate; the cycle prepays this many months.
function cycleMonths(billingCycle) {
  if (billingCycle === 'annual') return 12
  if (billingCycle === '6_months') return 6
  return 1
}

function billingCadenceLabel(plan) {
  if (!plan) return '—'
  const months = cycleMonths(plan.billing_cycle)
  if (months === 1) return 'Monthly'
  const upfront = formatIDR(Number(plan.platform_fee_idr || 0) * months)
  return `Prepay ${months} mo · ${upfront}`
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function StatusBadge({ status }) {
  const { cls, label } = invoiceStatusBadge(status)
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs-medium ${cls}`}
    >
      {label}
    </span>
  )
}

function SubscriptionStatusBadge({ status }) {
  const { cls, label } = subscriptionStatusBadge(status)
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs-medium ${cls}`}
    >
      {label}
    </span>
  )
}

function Billing({ companyId }) {
  const { openSubscribePanel, openTopupPanel, openInvoicePanel, companyDetail, billingRefreshKey } =
    useCompany()
  const navigate = useNavigate()
  const companyName = companyDetail?.legal_name || companyDetail?.name || null
  // Billing actions (subscribe / change plan / top up) require an ACTIVE
  // membership (CLIK-validated) — a company still onboarding / waiting approval /
  // expired can't transact. The BE enforces this too (requireActiveMembership).
  const isMemberActive = memberStatusOf(companyDetail) === 'active'
  const goToSubscribePage = () =>
    navigate(`/company/${companyId}/subscribe`, { state: { companyName } })
  const [balance, setBalance] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [canceling, setCanceling] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const load = async () => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    try {
      const [b, s, inv] = await Promise.all([
        BillingService.getBalance(companyId),
        BillingService.getSubscription(companyId),
        BillingService.getInvoices(companyId, { page: 1, limit: 20 }),
      ])
      // Balance row is lazy-created server-side, never null.
      setBalance(b?.data ?? b)
      // Subscription: server returns { status, message, data: null } when
      // there's no active sub. `data: null` is meaningful; do NOT fall back
      // to the response wrapper (would surface `status: 200` into UI).
      setSubscription(s && Object.prototype.hasOwnProperty.call(s, 'data') ? s.data : (s || null))
      setInvoices(inv?.data?.data ?? inv?.data ?? [])
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load billing')
    } finally {
      setLoading(false)
    }
  }

  // Re-fetch on companyId change AND whenever billingRefreshKey bumps (a panel
  // closed or a billing action completed) — so new invoices / subscription
  // status show without a manual page refresh.
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, billingRefreshKey])

  const usage = useMemo(() => {
    const total = Number(balance?.total_quota || 0)
    const used = Number(balance?.used_quota || 0)
    // held = quota reserved for in-flight enquiries (creation-timing model).
    // "left" is the spendable balance net of held; prefer the backend's
    // available_quota and fall back to total-used-held (held defaults 0 if a
    // pre-reservation backend omits it, degrading to the old total-used).
    const held = Number(balance?.held_quota || 0)
    const remaining = balance?.available_quota != null
      ? Math.max(0, Number(balance.available_quota))
      : Math.max(0, total - used - held)
    // Bar fills with committed quota (used + reserved) so its empty portion
    // matches the "left" figure.
    const committed = Math.min(total, used + held)
    const pct = total > 0 ? Math.min(100, Math.round((committed / total) * 100)) : 0
    return { total, used, held, remaining, pct }
  }, [balance])

  const handleCancelSubscription = async (immediate) => {
    setCanceling(true)
    try {
      await BillingService.cancelSubscription(companyId, { immediate, reason: null })
      setConfirmCancel(false)
      await load()
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to cancel subscription')
    } finally {
      setCanceling(false)
    }
  }

  const [resuming, setResuming] = useState(false)
  const handleResume = async () => {
    setResuming(true)
    setError(null)
    try {
      await BillingService.resumeSubscription(companyId)
      await load()
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to resume subscription')
    } finally {
      setResuming(false)
    }
  }

  const hasActiveSub = subscription && subscription.status === 'active'
  const isSuspended = subscription && subscription.status === 'suspended'
  const isPendingPayment = subscription && subscription.status === 'pending_payment'
  const sub = subscription

  // Parent MyTabPanel container is `overflow-hidden`, so content longer than
  // the panel height gets clipped. Wrap in SimpleBar (same pattern as Result
  // & Information tabs) to enable vertical scroll.
  return (
    <SimpleBar forceVisible="y" style={{ height: '100%' }}>
    <div className="flex flex-col gap-8 px-4 pb-12 pt-2">
      {error ? (
        <div className="rounded-md border border-error/200 bg-error/50 p-3 text-sm text-error/700">
          {error}
        </div>
      ) : null}

      {/* ─── Subscription card ──────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-light/200 bg-white shadow-xs">
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg-semibold text-gray-light/900">Subscription</h3>
              <p className="text-sm-regular text-gray-light/600">
                Current plan & billing period.
              </p>
            </div>
            {sub ? <SubscriptionStatusBadge status={sub.status} /> : null}
          </div>

          {loading ? (
            <div className="h-16 animate-pulse rounded-md bg-gray-light/100" />
          ) : sub ? (
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-light/600">Plan</span>
                <span className="text-sm-semibold text-gray-light/900">
                  {sub.pricing_plan?.name || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-light/600">Per enquiry</span>
                <span className="text-gray-light/700">
                  {formatIDR(sub.pricing_plan?.per_enquiry_fee_idr)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-light/600">Platform / month</span>
                <span className="text-gray-light/700">
                  {formatIDR(sub.pricing_plan?.platform_fee_idr)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-light/600">Billing</span>
                <span className="text-gray-light/700">
                  {billingCadenceLabel(sub.pricing_plan)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-light/600">Current period ends</span>
                {/*
                  `covered_until` = period_end of the newest PAID invoice, which
                  is the question this row asks. Both `current_period_*` fields
                  are billing POINTERS advanced after each invoice generates:
                  `_end` sits a full cycle out, and `_start` only coincides with
                  real coverage while nothing else has moved it. Fall back to it
                  for subscriptions predating the field.
                */}
                <span className="text-gray-light/700">
                  {formatDate(sub.covered_until || sub.current_period_start)}
                </span>
              </div>
              {sub.commitment_until ? (
                <div className="flex justify-between">
                  <span className="text-gray-light/600">Commitment until</span>
                  <span className="text-gray-light/700">{formatDate(sub.commitment_until)}</span>
                </div>
              ) : null}
              {sub.cancel_at_period_end ? (
                <p className="mt-1 text-xs-regular text-warning/700">
                  Will end at {formatDate(sub.covered_until || sub.current_period_start)}.
                </p>
              ) : null}
              {isSuspended ? (
                <p className="mt-1 text-xs-regular text-warning/700">
                  Subscription suspended — pay outstanding invoice to resume.
                </p>
              ) : null}
              {isPendingPayment ? (
                <p className="mt-1 text-xs-regular text-warning/700">
                  Awaiting first payment — pay the platform fee invoice below to
                  activate. Quota &amp; enquiry are disabled until then.
                </p>
              ) : null}
              {sub.pending_plan_id ? (
                <p className="mt-1 text-xs-regular text-brand/700">
                  Plan change pending — current plan stays active until you pay the
                  new invoice below, then it switches automatically.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <p className="text-sm-regular text-gray-light/600">
                No active subscription. Subscribe to a plan to start using EBC service.
              </p>
              {!isMemberActive ? (
                <p className="text-xs-regular text-warning/700">
                  Company isn’t active yet — validate the CLIK credentials (Result tab) before
                  subscribing to a plan.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="border-t border-gray-light/200 px-6 py-4">
          <div className="flex flex-wrap justify-end gap-2">
            {/* Resume — undo a scheduled cancel-at-period-end */}
            {hasActiveSub && sub.cancel_at_period_end ? (
              <button
                type="button"
                onClick={handleResume}
                disabled={resuming}
                className="rounded-lg bg-brand/600 px-3.5 py-2.5 text-sm-semibold text-white shadow-xs hover:bg-brand/700 disabled:opacity-50"
              >
                {resuming ? 'Resuming…' : 'Resume subscription'}
              </button>
            ) : null}
            {/* Change plan — only while active and not pending the end */}
            {hasActiveSub && !sub.cancel_at_period_end ? (
              <button
                type="button"
                onClick={openSubscribePanel}
                disabled={loading || !isMemberActive}
                title={!isMemberActive ? 'Company must be active to change plan' : undefined}
                className="rounded-lg border border-brand/300 bg-white px-3.5 py-2.5 text-sm-semibold text-brand/700 shadow-xs hover:bg-brand/50 disabled:opacity-50"
              >
                Change plan
              </button>
            ) : null}
            {(hasActiveSub || isPendingPayment) && !sub.cancel_at_period_end ? (
              <button
                type="button"
                onClick={() => setConfirmCancel(true)}
                disabled={canceling}
                className="rounded-lg border border-gray-light/300 bg-white px-3.5 py-2.5 text-sm-semibold text-gray-light/700 shadow-xs hover:bg-gray-light/50 disabled:opacity-50"
              >
                {isPendingPayment ? 'Cancel' : 'Cancel subscription'}
              </button>
            ) : null}
            {!sub ? (
              <button
                type="button"
                onClick={goToSubscribePage}
                disabled={loading || !isMemberActive}
                title={!isMemberActive ? 'Company must be active (CLIK credentials validated) before subscribing' : undefined}
                className="rounded-lg bg-brand/600 px-3.5 py-2.5 text-sm-semibold text-white shadow-xs hover:bg-brand/700 disabled:opacity-50"
              >
                Subscribe
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {/* ─── Inline cancel confirmation ─────────────────────────────────── */}
      {confirmCancel ? (
        <section className="rounded-xl border border-warning/200 bg-warning/50 p-4">
          <p className="text-sm-semibold text-warning/700">
            {isPendingPayment ? 'Cancel this subscription?' : 'Cancel subscription?'}
          </p>
          <p className="mt-1 text-sm-regular text-warning/700">
            {isPendingPayment
              ? 'Not paid yet — this subscription will be canceled immediately and the unpaid platform fee invoice voided.'
              : `Default: end at current period (${formatDate(sub?.current_period_start)}). You can also terminate immediately.`}
          </p>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmCancel(false)}
              disabled={canceling}
              className="rounded-lg border border-gray-light/300 bg-white px-3 py-2 text-sm-semibold text-gray-light/700 hover:bg-gray-light/50"
            >
              Back
            </button>
            {isPendingPayment ? (
              <button
                type="button"
                onClick={() => handleCancelSubscription(true)}
                disabled={canceling}
                className="rounded-lg bg-error/600 px-3 py-2 text-sm-semibold text-white shadow-xs hover:bg-error/700 disabled:opacity-50"
              >
                {canceling ? 'Canceling…' : 'Cancel subscription'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleCancelSubscription(true)}
                  disabled={canceling}
                  className="rounded-lg border border-error/300 bg-white px-3 py-2 text-sm-semibold text-error/700 hover:bg-error/50"
                >
                  Cancel immediately
                </button>
                <button
                  type="button"
                  onClick={() => handleCancelSubscription(false)}
                  disabled={canceling}
                  className="rounded-lg bg-warning/600 px-3 py-2 text-sm-semibold text-white shadow-xs hover:bg-warning/700"
                >
                  {canceling ? 'Saving…' : 'End at period'}
                </button>
              </>
            )}
          </div>
        </section>
      ) : null}

      {/* ─── Enquiry Quota card ─────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-light/200 bg-white shadow-xs">
        <div className="flex flex-col gap-5 p-6">
          <div>
            <h3 className="text-lg-semibold text-gray-light/900">Enquiry Quota</h3>
            <p className="text-sm-regular text-gray-light/600">
              Number of usage &amp; remaining enquiries.
            </p>
          </div>

          {loading ? (
            <div className="h-12 animate-pulse rounded-md bg-gray-light/100" />
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-4 text-sm leading-5">
                <p className="font-medium text-gray-light/700">
                  {usage.used.toLocaleString('id-ID')} of {usage.total.toLocaleString('id-ID')}{' '}
                  enquiry used
                </p>
                <p className="text-gray-light/600">{usage.remaining.toLocaleString('id-ID')} left</p>
              </div>
              <div className="h-2 w-full overflow-hidden rounded bg-gray-light/200">
                <div
                  className="h-2 rounded bg-brand/600 transition-all"
                  style={{ width: `${usage.pct}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-light/200 px-6 py-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={openTopupPanel}
              disabled={loading || !hasActiveSub || !isMemberActive}
              title={
                !isMemberActive
                  ? 'Company must be active to top up'
                  : !hasActiveSub
                    ? 'Subscribe first to enable top up'
                    : undefined
              }
              className="rounded-lg bg-brand/600 px-3.5 py-2.5 text-sm-semibold text-white shadow-xs hover:bg-brand/700 disabled:opacity-50"
            >
              Top up quota
            </button>
          </div>
        </div>
      </section>

      {/* ─── Quota usage (ledger + holds) ───────────────────────────────────
          Provisional entrypoint — self-contained, remount elsewhere once
          UI/UX decides where the "penggunaan saldo" menu lives. */}
      <QuotaUsage companyId={companyId} />

      {/* ─── Billing & invoicing ────────────────────────────────────────── */}
      <section className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg-semibold text-gray-light/900">Billing and invoicing</h3>
            <p className="text-sm-regular text-gray-light/600">History of billing and invoice.</p>
          </div>
          {invoices.length > 0 ? (
            <button
              type="button"
              onClick={() => downloadInvoiceStatement(invoices, companyDetail)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-light/300 bg-white px-3 py-2 text-sm-semibold text-gray-light/700 shadow-xs hover:bg-gray-light/50"
            >
              <Download01 size={16} stroke="currentColor" />
              Download all
            </button>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-md border border-gray-light/200 bg-white shadow-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-light/200 bg-white">
                <th className="px-6 py-3 text-left text-xs-semibold text-gray-light/500">Invoice</th>
                <th className="px-6 py-3 text-right text-xs-semibold text-gray-light/500">Total</th>
                <th className="px-3 py-3 text-left text-xs-semibold text-gray-light/500">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-6 text-center text-sm text-gray-light/500">
                    Loading…
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-6 text-center text-sm text-gray-light/500">
                    No invoices yet
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => openInvoicePanel(inv.id)}
                    className="cursor-pointer border-b border-gray-light/200 last:border-b-0 hover:bg-gray-light/50"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm-medium text-gray-light/900">{inv.invoice_number}</div>
                      <div className="text-sm-regular text-gray-light/600">
                        {formatDate(inv.created_at)}
                      </div>
                      <div className="text-xs-regular text-gray-light/500">
                        {inv.invoice_type === 'subscription'
                          ? 'Platform fee'
                          : inv.invoice_type === 'topup'
                            ? `Top up ${inv.quota_amount} enquiry`
                            : 'Quota purchase'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm-regular text-gray-light/600">
                      {formatIDR(inv.total_idr)}
                    </td>
                    <td className="px-3 py-4">
                      <StatusBadge status={inv.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
    </SimpleBar>
  )
}

export default Billing
